import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, MapPin, Phone, Ship } from "lucide-react";
import Seo, { marinaJsonLd } from "../components/Seo";
import PageHero from "../components/PageHero";
import FleetStrip from "../components/FleetStrip";
import WeatherBanner from "../components/WeatherBanner";
import { Section, SectionHeading, InfoCard } from "../components/Section";
import { todayISO } from "../components/BookingWidget";
import { LOCATIONS, directionsUrl } from "../lib/content";
import { useHours } from "../hooks/useHours";

const WB = LOCATIONS["west-branch"];

export default function WestBranch() {
  const [params, setParams] = useSearchParams();
  const min = todayISO();
  const qsDate = params.get("date");
  const initial = qsDate && qsDate >= min ? qsDate : min;
  const [date, setDate] = useState(initial);
  const dateRef = useRef(null);
  const { hoursFor } = useHours();
  const h = hoursFor("west-branch");

  useEffect(() => {
    if (qsDate !== date) {
      const next = new URLSearchParams(params);
      next.set("date", date);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const focusDate = () => {
    dateRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    dateRef.current?.focus();
  };

  return (
    <>
      <Seo
        title="West Branch State Park Boat Rentals | Live Availability"
        description="See live boat availability at West Branch State Park and reserve pontoons, tritoons, jet skis and kayaks. Call (330) 296-9209 for help."
        path="/boat-rentals/west-branch"
        image="/images/family_pontoon_deck.jpeg"
        jsonLd={marinaJsonLd(WB)}
      />

      <PageHero
        image="/images/family_pontoon_deck.jpeg"
        alt="Family enjoying a sunny afternoon on a pontoon deck"
        eyebrow="West Branch State Park"
        title="West Branch boat rentals"
        subtitle="Choose a date and we'll show you every boat that can leave the dock that day, straight from our booking system."
      />

      <div className="sticky top-16 lg:top-20 z-30 border-b border-sand-border bg-sand/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-10">
          <div className="flex items-center gap-3">
            <label htmlFor="wb-date" className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Rental date
            </label>
            <div className="relative">
              <CalendarDays
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <input
                id="wb-date"
                ref={dateRef}
                type="date"
                data-testid="west-branch-date-input"
                value={date}
                min={min}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="h-11 rounded-xl border border-sand-border bg-white pl-9 pr-3 text-sm font-semibold text-navy"
              />
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-600" data-testid="west-branch-hours">
              {h ? `Open daily ${h.open} – ${h.close}` : "Hours loading…"}
            </span>
            <a
              href={WB.phoneHref}
              data-testid="west-branch-phone-link"
              className="inline-flex items-center gap-2 font-bold text-navy hover:text-ember transition-colors"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              {WB.phone}
            </a>
          </div>
        </div>
      </div>

      <FleetStrip
        locationId={WB.locationId}
        slug={WB.slug}
        date={date}
        onChangeDate={focusDate}
        showLocationPicker={false}
        heading={`Boats available on ${date}`}
        intro="Every card is a real boat in our fleet. Pick one to choose your hours, departure time and gear."
      />

      <Section tone="white">
        <SectionHeading
          eyebrow="At the marina"
          title="What's here when you arrive"
          intro="West Branch State Park has five launch ramps; the East and West ramps offer courtesy docks."
        />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard icon={MapPin} title="Where to find us" testId="wb-card-address">
            <span data-testid="wb-address">
              {WB.streetAddress}
              <br />
              {WB.city}, {WB.region} {WB.postalCode}
            </span>
            <a
              href={directionsUrl(WB)}
              target="_blank"
              rel="noreferrer"
              data-testid="wb-directions-link"
              className="mt-2 inline-block font-bold text-lake hover:underline"
            >
              Get directions
            </a>
          </InfoCard>
          <InfoCard icon={Ship} title="Five launch ramps" testId="wb-card-ramps">
            Five public launch ramps serve the lake. Courtesy docks are available at the East and West ramps.
          </InfoCard>
          <InfoCard icon={MapPin} title="Courtesy dockage" testId="wb-card-dockage">
            Courtesy docks are $20 per night.{" "}
            <Link to="/storage-and-docking" data-testid="wb-card-dockage-link" className="font-bold text-lake hover:underline">
              Storage &amp; docking details
            </Link>
            .
          </InfoCard>
          <InfoCard icon={Phone} title="Questions before you go" testId="wb-card-contact">
            Call{" "}
            <a href={WB.phoneHref} data-testid="wb-card-contact-phone" className="font-bold text-lake hover:underline">
              {WB.phone}
            </a>{" "}
            and we'll talk you through boats, towing rules and the weather.
          </InfoCard>
        </div>
      </Section>

      <WeatherBanner location="west-branch" title="West Branch Marina weather" />
    </>
  );
}
